import React, { useMemo } from 'react';
import { Handle, NodeProps, Position } from 'reactflow';
import {
  buildFlowFxPreviewLines,
  flowFxBadge,
  flowFxHasSourceHandle,
  flowFxHasTargetHandle,
  type FlowFxPersistedData,
} from '../flowFxWorkflowTypes';
import './FlowFxNode.css';

export type FlowFxNodeData = FlowFxPersistedData & {
  incomingInputHint?: string;
};

/** Evenly spaced percentages along the vertical edge (top → bottom), for stacked handles */
function evenVerticalPercent(index: number, total: number): string {
  if (total <= 1) return '50%';
  return `${((index + 1) / (total + 1)) * 100}%`;
}

export default function FlowFxNode({ data, selected }: NodeProps<FlowFxNodeData>) {
  const { kind } = data;
  const badge = flowFxBadge(kind);
  const previews = useMemo(() => buildFlowFxPreviewLines(data), [data]);

  const showTarget = flowFxHasTargetHandle(kind);
  const showSource = flowFxHasSourceHandle(kind);

  const variantClass =
    kind === 'comment'
      ? ` flow-fx-node--comment flow-fx-node--accent-${data.commentAccent}`
      : kind === 'error-handler'
        ? ' flow-fx-node--error'
        : data.categoryTag === 'utility'
          ? ' flow-fx-node--utility'
          : ' flow-fx-node--flow';

  const width = useMemo(() => {
    if (kind === 'switch') {
      const n = Math.max(2, data.switchCases.length + 1);
      return Math.min(200, 140 + Math.min(n, 5) * 14);
    }
    if (kind === 'parallel') {
      return Math.min(200, 130 + data.parallelBranchCount * 18);
    }
    return 168;
  }, [kind, data.switchCases.length, data.parallelBranchCount]);

  const mergeCount = kind === 'merge' ? data.mergeInputCount : 0;

  const bottomLayout = useMemo(() => {
    if (!showSource) return { ids: [] as string[], labels: [] as string[] };
    switch (kind) {
      case 'if-else':
        return { ids: ['out-true', 'out-false'], labels: [data.ifTrueLabel, data.ifFalseLabel] };
      case 'error-handler':
        return { ids: ['out-success', 'out-error'], labels: ['Success', 'Error'] };
      case 'loop':
        return { ids: ['out-body', 'out-complete'], labels: ['Loop body', 'Complete'] };
      case 'switch': {
        const ids = data.switchCases.map((c, i) => `out-case-${i}`);
        ids.push('out-default');
        const labels = data.switchCases.map((c) => c.label || c.match || `Case ${c.match}`);
        labels.push('Default');
        return { ids, labels };
      }
      case 'parallel': {
        const n = data.parallelBranchCount;
        const ids = Array.from({ length: n }, (_, i) => `out-path-${i}`);
        const labels = data.parallelLabels.slice(0, n).map((l, i) => l || `Path ${i + 1}`);
        return { ids, labels };
      }
      default:
        return { ids: ['out'], labels: [''] };
    }
  }, [showSource, kind, data]);

  const { ids: sourceIds, labels: sourceLabels } = bottomLayout;
  const nOut = sourceIds.length;
  const nInMerge = kind === 'merge' ? mergeCount : 0;

  return (
    <div
      className={`flow-fx-node${variantClass}${selected ? ' flow-fx-node--selected' : ''}${nOut > 1 ? ' flow-fx-node--multi-out' : ''}${nInMerge > 1 ? ' flow-fx-node--multi-in' : ''}`}
      style={{ width, minHeight: Math.max(88, 36 + Math.max(nOut, nInMerge || 1) * 32) }}
    >
      {/* Targets: left edge, vertically stacked (single or merge) */}
      {showTarget && kind !== 'merge' && (
        <Handle
          type="target"
          position={Position.Left}
          id="in"
          className="flow-fx-node__handle flow-fx-node__handle--target flow-fx-node__handle--left"
          style={{ top: '50%' }}
        />
      )}

      {showTarget &&
        kind === 'merge' &&
        Array.from({ length: mergeCount }, (_, i) => (
          <Handle
            key={`in-${i}`}
            type="target"
            position={Position.Left}
            id={`in-${i}`}
            className="flow-fx-node__handle flow-fx-node__handle--target flow-fx-node__handle--left"
            style={{ top: evenVerticalPercent(i, mergeCount) }}
          />
        ))}

      <div className="flow-fx-node__header">
        <span className="flow-fx-node__dot" aria-hidden />
        <div className="flow-fx-node__head-text">
          <div className="flow-fx-node__title">{data.label}</div>
        </div>
        <span className="flow-fx-node__badge">{badge}</span>
      </div>

      <div className="flow-fx-node__body nodrag">
        {previews.map((line, i) => (
          <div key={i} className="flow-fx-node__preview-line">
            {line}
          </div>
        ))}
      </div>

      {/* Sources: right edge, vertically stacked */}
      {showSource &&
        nOut > 0 &&
        sourceIds.map((id, i) => (
          <React.Fragment key={id}>
            {sourceLabels[i] ? (
              <span
                className="flow-fx-node__out-label flow-fx-node__out-label--side"
                style={{
                  top: evenVerticalPercent(i, nOut),
                }}
              >
                {sourceLabels[i]}
              </span>
            ) : null}
            <Handle
              type="source"
              position={Position.Right}
              id={id}
              className="flow-fx-node__handle flow-fx-node__handle--source flow-fx-node__handle--right"
              style={{ top: evenVerticalPercent(i, nOut) }}
            />
          </React.Fragment>
        ))}
    </div>
  );
}
